/**
 * Bidirectional folder sync between a local FileSystemDirectoryHandle and the
 * session workspace on the backend.
 *
 * - local → server: FileSystemObserver (Chrome 133+) or 3s polling fallback,
 *   500ms debounce, SHA-256 hashing via worker, diff-based upload.
 * - server → local: SSE /files/watch with seq-based reconnect. On `resync`
 *   we run a full forwardSync to reconcile from scratch.
 *
 * Echo suppression has two sides:
 *   - Server → client: every mutating REST call carries an `X-Op-Id`; the
 *     watcher tags the SSE echo with that id, and we drop events whose opId
 *     we originated (`pendingOpsRef`).
 *   - Local FS → us: when we write/delete files locally in response to a
 *     server event, we mark the path in `suppressLocalRef`; the observer
 *     event that follows consumes the mark and is ignored.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearWorkspace,
  deleteFiles as deleteFilesApi,
  getFileContent,
  mkdirRemote,
  rmdirRemote,
  subscribeFileWatch,
  syncDiff,
  uploadFiles,
  type FileWatchSubscription,
  type ManifestEntry,
  type SyncDiffResult,
  type UploadEntry,
} from "../api/files";
import { hashBuffer, hashString, loadCache, replaceCache, type CacheEntry } from "../lib/syncCache";
import { isAttachmentPath } from "../lib/attachments";

/** Drop one-shot attachments from a diff's server-only side. A connected
 *  folder mirrors the workspace root, so without this it would see the
 *  `attachments/` files (which the folder doesn't contain) as deletions and
 *  wipe them — and create a stray `attachments/` dir in the user's folder.
 *  Only the server-only side is filtered: if the user's own folder genuinely
 *  contains an `attachments/` path it lands in the manifest, stays matched/
 *  different/onlyLocal, and syncs normally. */
function stripAttachments(diff: SyncDiffResult): SyncDiffResult {
  return {
    ...diff,
    onlyServer: diff.onlyServer.filter((e) => !isAttachmentPath(e.path)),
    dirsOnlyServer: diff.dirsOnlyServer.filter((d) => !isAttachmentPath(d)),
  };
}

const SKIP_DIRS = new Set(["node_modules", ".git", "__pycache__", ".venv"]);
const SKIP_FILES = new Set([".DS_Store", "Thumbs.db", "desktop.ini"]);
const SKIP_EXTENSIONS = new Set(["crswap", "crdownload", "tmp", "swp", "swo", "swx"]);
const BINARY_EXTENSIONS = new Set(["docx", "xlsx", "pptx", "pdf", "zip", "png", "jpg", "jpeg", "gif"]);

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const MAX_TOTAL_SIZE = 200 * 1024 * 1024;

const DEBOUNCE_MS = 500;
const POLL_INTERVAL_MS = 3000;

declare global {
  interface FileSystemObserverRecord {
    type: "appeared" | "disappeared" | "modified" | "moved" | "unknown" | "errored";
    changedHandle: FileSystemHandle | null;
    relativePathComponents: string[];
    root: FileSystemHandle;
  }
  class FileSystemObserver {
    constructor(callback: (records: FileSystemObserverRecord[], observer: FileSystemObserver) => void);
    observe(handle: FileSystemHandle, options?: { recursive?: boolean }): Promise<void>;
    disconnect(): void;
  }
}

async function hashData(data: Blob | string): Promise<string> {
  if (typeof data === "string") return hashString(data);
  return hashBuffer(await data.arrayBuffer());
}

async function hashContent(content: string, encoding?: string): Promise<string> {
  if (encoding === "base64") {
    const bin = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    return hashBuffer(bin.buffer);
  }
  return hashString(content);
}

function nfc(s: string): string {
  return s.normalize("NFC");
}

function shouldSkipFile(name: string): boolean {
  if (SKIP_FILES.has(name)) return true;
  const ext = name.split(".").pop()?.toLowerCase() || "";
  return SKIP_EXTENSIONS.has(ext);
}

async function listDirPaths(dir: FileSystemDirectoryHandle, prefix = ""): Promise<string[]> {
  const paths: string[] = [];
  const entries = (dir as unknown as { entries: () => AsyncIterable<[string, FileSystemHandle]> }).entries();
  for await (const [name, handle] of entries) {
    if (SKIP_DIRS.has(name) || shouldSkipFile(name)) continue;
    const path = nfc(prefix ? `${prefix}/${name}` : name);
    if (handle.kind === "directory") {
      const children = await listDirPaths(handle as FileSystemDirectoryHandle, path);
      if (children.length === 0) paths.push(path + "/");
      else paths.push(...children);
    } else {
      paths.push(path);
    }
  }
  return paths;
}

interface ManifestFile {
  entry: ManifestEntry;
  mtime: number;
  fileHandle: FileSystemFileHandle;
}

interface Manifest {
  files: Map<string, ManifestFile>;
  dirs: string[];
  totalSize: number;
  skippedLarge: string[];
}

interface WalkItem {
  path: string;
  handle: FileSystemFileHandle;
}

async function walkDir(
  dir: FileSystemDirectoryHandle,
  prefix: string,
  files: WalkItem[],
  dirs: string[],
): Promise<void> {
  let hasChild = false;
  for await (const [name, handle] of (dir as unknown as { entries: () => AsyncIterable<[string, FileSystemHandle]> }).entries()) {
    if (SKIP_DIRS.has(name) || shouldSkipFile(name)) continue;
    hasChild = true;
    const path = nfc(prefix ? `${prefix}/${name}` : name);
    if (handle.kind === "directory") {
      await walkDir(handle as FileSystemDirectoryHandle, path, files, dirs);
    } else {
      files.push({ path, handle: handle as FileSystemFileHandle });
    }
  }
  if (!hasChild && prefix) dirs.push(prefix);
}

async function buildManifest(
  dir: FileSystemDirectoryHandle,
  sessionKey: string,
  prehashed?: Map<string, CacheEntry>,
): Promise<Manifest> {
  const files: WalkItem[] = [];
  const dirs: string[] = [];
  await walkDir(dir, "", files, dirs);

  const cached = await loadCache(sessionKey, files.map(f => f.path));
  const manifest: Manifest = { files: new Map(), dirs, totalSize: 0, skippedLarge: [] };

  await Promise.all(files.map(async ({ path, handle }) => {
    try {
      const file = await handle.getFile();
      if (file.size > MAX_FILE_SIZE) {
        manifest.skippedLarge.push(path);
        return;
      }
      manifest.totalSize += file.size;

      const c = cached.get(path) ?? prehashed?.get(path);
      let hash: string;
      if (c && c.mtime === file.lastModified && c.size === file.size) {
        hash = c.hash;
      } else {
        hash = await hashBuffer(await file.arrayBuffer());
      }
      manifest.files.set(path, {
        entry: { path, hash, size: file.size },
        mtime: file.lastModified,
        fileHandle: handle,
      });
    } catch {}
  }));

  const entries: CacheEntry[] = [...manifest.files.values()].map(f => ({
    path: f.entry.path,
    mtime: f.mtime,
    size: f.entry.size,
    hash: f.entry.hash,
  }));
  replaceCache(sessionKey, entries).catch(() => {});

  return manifest;
}

async function readForUpload(mf: ManifestFile): Promise<Blob> {
  const file = await mf.fileHandle.getFile();
  return file.slice(0, file.size, file.type);
}

function checkBudget(m: Manifest): { ok: boolean; warning: string } {
  if (m.totalSize > MAX_TOTAL_SIZE) {
    return {
      ok: false,
      warning: `Общий объём файлов (${(m.totalSize / 1024 / 1024).toFixed(1)} МБ) превышает лимит ${MAX_TOTAL_SIZE / 1024 / 1024} МБ. Синхронизация отменена.`,
    };
  }
  return {
    ok: true,
    warning: m.skippedLarge.length > 0
      ? `Пропущены файлы > ${MAX_FILE_SIZE / 1024 / 1024} МБ: ${m.skippedLarge.join(", ")}`
      : "",
  };
}

export interface UploadProgress {
  current: number;
  total: number;
}

/** Pre-session-friendly folder upload — manifest → /sync/diff → batched
 *  POST /files, without React state or watcher coupling. Called from
 *  handleSend so /api/runs only fires after the agent's workspace is
 *  populated. Once the new ChatPage mounts and useDocSync calls
 *  finishConnect("merge"), the manifest matches the server, syncDiff is a
 *  no-op, watchers start, and nothing is re-uploaded. */
export async function uploadFolderToSession(
  sessionId: string,
  handle: FileSystemDirectoryHandle,
  onProgress?: (p: UploadProgress) => void,
): Promise<{ warning: string }> {
  const prehashed = consumePrehashCache(handle);
  const m = await buildManifest(handle, sessionId, prehashed ?? undefined);
  const budget = checkBudget(m);
  if (!budget.ok) return { warning: budget.warning };

  const opId = crypto.randomUUID();
  const clientManifest = [...m.files.values()].map((f) => f.entry);
  const diff = stripAttachments(await syncDiff(sessionId, clientManifest, m.dirs));

  const uploadPaths = [
    ...diff.onlyLocal.map((e) => e.path),
    ...diff.different.map((e) => e.path),
  ];
  const total = uploadPaths.length;
  onProgress?.({ current: 0, total });

  // Honest progress: report AFTER each batch upload completes, not during
  // the (sub-ms) read loop. Reporting after reads filled the bar before
  // a single byte hit the wire, so the user saw it race to the end and
  // then wait for the actual upload + sendMessage with no feedback.
  // Batch size of 5 keeps ticks visible for small uploads too.
  const BATCH = 5;
  for (let i = 0; i < total; i += BATCH) {
    const batchPaths = uploadPaths.slice(i, i + BATCH);
    const batch: UploadEntry[] = [];
    for (const p of batchPaths) {
      const f = m.files.get(p);
      if (f) batch.push({ path: p, data: await readForUpload(f) });
    }
    if (batch.length > 0) await uploadFiles(sessionId, batch, opId);
    onProgress?.({ current: Math.min(i + BATCH, total), total });
  }

  if (diff.onlyServer.length > 0) {
    await deleteFilesApi(sessionId, diff.onlyServer.map((e) => e.path), opId);
  }
  for (const d of diff.dirsOnlyLocal) {
    try { await mkdirRemote(sessionId, d, opId); } catch {}
  }
  for (const d of diff.dirsOnlyServer) {
    try { await rmdirRemote(sessionId, d, opId); } catch {}
  }

  return { warning: budget.warning };
}

/** In-memory hash cache populated on hero folder-pick. Consumed once by
 *  uploadFolderToSession so the real upload doesn't re-read every byte. */
const handlePrehash = new WeakMap<FileSystemDirectoryHandle, Map<string, CacheEntry>>();

export async function prehashFolder(handle: FileSystemDirectoryHandle): Promise<void> {
  const files: WalkItem[] = [];
  const dirs: string[] = [];
  try {
    await walkDir(handle, "", files, dirs);
  } catch { return; }

  const out = new Map<string, CacheEntry>();
  await Promise.all(files.map(async ({ path, handle: fh }) => {
    try {
      const file = await fh.getFile();
      if (file.size > MAX_FILE_SIZE) return;
      const hash = await hashBuffer(await file.arrayBuffer());
      out.set(path, { path, mtime: file.lastModified, size: file.size, hash });
    } catch {}
  }));
  handlePrehash.set(handle, out);
}

function consumePrehashCache(handle: FileSystemDirectoryHandle): Map<string, CacheEntry> | null {
  const c = handlePrehash.get(handle) ?? null;
  if (c) handlePrehash.delete(handle);
  return c;
}

async function getFileByPath(dir: FileSystemDirectoryHandle, path: string): Promise<File | null> {
  const parts = path.split("/");
  let current = dir;
  for (let i = 0; i < parts.length - 1; i++) {
    try {
      current = await current.getDirectoryHandle(parts[i]!);
    } catch {
      return null;
    }
  }
  try {
    const fh = await current.getFileHandle(parts[parts.length - 1]!);
    return await fh.getFile();
  } catch {
    return null;
  }
}

async function writeFileToLocal(
  dir: FileSystemDirectoryHandle,
  path: string,
  content: string,
  encoding?: string,
): Promise<void> {
  const parts = path.split("/");
  let current = dir;
  for (let i = 0; i < parts.length - 1; i++) {
    current = await current.getDirectoryHandle(parts[i]!, { create: true });
  }
  const fh = await current.getFileHandle(parts[parts.length - 1]!, { create: true });
  const writable = await (fh as unknown as { createWritable: () => Promise<FileSystemWritableFileStream> }).createWritable();
  if (encoding === "base64") {
    const binary = Uint8Array.from(atob(content), c => c.charCodeAt(0));
    await writable.write(binary);
  } else {
    await writable.write(content);
  }
  await writable.close();
}

async function deleteLocalFile(dir: FileSystemDirectoryHandle, path: string): Promise<void> {
  const parts = path.split("/");
  let current = dir;
  for (let i = 0; i < parts.length - 1; i++) {
    try {
      current = await current.getDirectoryHandle(parts[i]!);
    } catch {
      return;
    }
  }
  try {
    await current.removeEntry(parts[parts.length - 1]!);
  } catch {}
}

export interface SyncStatus {
  active: boolean;
  direction: "upload" | "download" | "";
  current: number;
  total: number;
  warning: string;
}

export interface DocSyncState {
  dirHandle: FileSystemDirectoryHandle | null;
  /**
   * Session id this handle was connected for. ChatPage uses this to guard
   * persist/restore against a render where `sessionId` already changed but
   * the disconnect setState hasn't applied yet — without this guard we'd
   * save the previous session's handle under the new session's IndexedDB key.
   */
  connectedSessionId: string | null;
  docFiles: string[];
  docConnected: boolean;
  uploadedPaths: Set<string>;
  syncStatus: SyncStatus;
  conflict: { handle: FileSystemDirectoryHandle; serverCount: number } | null;
  pickFolder: () => Promise<void>;
  disconnect: () => void;
  reverseSync: () => Promise<void>;
  finishConnect: (handle: FileSystemDirectoryHandle, mode: "local" | "server" | "merge") => Promise<void>;
  dismissConflict: () => void;
  dismissWarning: () => void;
}

/**
 * @param sessionId current session id (workspace key). Pass null before a
 *                  session exists — the hook becomes a no-op until it shows up.
 * @param streaming whether the agent is running right now; when it flips
 *                  from true → false we trigger a forwardSync as safety net.
 */
export function useDocSync(sessionId: string | null, streaming: boolean): DocSyncState {
  const [dirHandle, setDirHandle] = useState<FileSystemDirectoryHandle | null>(null);
  const [connectedSessionId, setConnectedSessionId] = useState<string | null>(null);
  const [docFiles, setDocFiles] = useState<string[]>([]);
  const [docConnected, setDocConnected] = useState(false);
  const [conflict, setConflict] = useState<{ handle: FileSystemDirectoryHandle; serverCount: number } | null>(null);
  const [uploadedPaths, setUploadedPaths] = useState<Set<string>>(new Set());
  const [syncStatus, setSyncStatus] = useState<SyncStatus>({ active: false, direction: "", current: 0, total: 0, warning: "" });

  const snapshotRef = useRef<Record<string, string>>({});
  const dirHandleRef = useRef<FileSystemDirectoryHandle | null>(null);

  // opId → timestamp. We generate an opId per mutating REST call; the backend
  // watcher tags the resulting SSE event with it. Seeing our own opId means
  // the event is an echo of our own write and must be ignored.
  const pendingOpsRef = useRef<Map<string, number>>(new Map());
  const newOpId = (): string => {
    const id = crypto.randomUUID();
    const now = Date.now();
    pendingOpsRef.current.set(id, now);
    const cutoff = now - 30000;
    for (const [k, ts] of pendingOpsRef.current) if (ts < cutoff) pendingOpsRef.current.delete(k);
    return id;
  };
  const isOwnOp = (opId: string | undefined): boolean => {
    if (!opId) return false;
    return pendingOpsRef.current.delete(opId);
  };

  // path → timestamp. When we mutate the local FS in response to a server
  // event, we mark the path; the FileSystemObserver event that follows is
  // consumed and skipped. 10s TTL covers observer latency.
  const suppressLocalRef = useRef<Map<string, number>>(new Map());
  const suppressLocal = (path: string) => {
    const now = Date.now();
    suppressLocalRef.current.set(path, now);
    const cutoff = now - 10000;
    for (const [k, ts] of suppressLocalRef.current) if (ts < cutoff) suppressLocalRef.current.delete(k);
  };
  const consumeLocalSuppress = (path: string): boolean => suppressLocalRef.current.delete(path);

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const sid = () => sessionIdRef.current;

  const syncEpochRef = useRef(0);
  const observerRef = useRef<FileSystemObserver | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  const fileWatchRef = useRef<FileWatchSubscription | null>(null);
  const localDebounceRef = useRef<number | null>(null);
  const containerDebounceRef = useRef<number | null>(null);
  const pendingLocalRef = useRef<Map<string, { event: string; isDir: boolean }>>(new Map());
  const pendingContainerRef = useRef<Map<string, { event: string; isDir: boolean }>>(new Map());

  // Promise-chain queue — all four sync ops (reverse/forward/processLocal/
  // processContainer) run strictly serialized. Coalescing of repeated
  // triggers is handled upstream by the debounced pending maps.
  const syncChainRef = useRef<Promise<void>>(Promise.resolve());
  const enqueueSync = <T,>(task: () => Promise<T>): Promise<T> => {
    const next = syncChainRef.current.then(task, task);
    syncChainRef.current = next.then(() => {}, () => {});
    return next;
  };

  const refreshFileList = useCallback(async () => {
    const handle = dirHandleRef.current;
    if (!handle) return;
    const allPaths = await listDirPaths(handle);
    setDocFiles(allPaths);
  }, []);

  const startSyncStatus = useCallback((direction: "upload" | "download", total: number) => {
    const t0 = Date.now();
    const myEpoch = syncEpochRef.current;
    const timer = window.setTimeout(() => {
      if (syncEpochRef.current !== myEpoch) return;
      setSyncStatus(prev => ({ ...prev, active: true, direction, total }));
    }, 1000);
    return {
      update: (current: number) => {
        if (syncEpochRef.current !== myEpoch) return;
        if (Date.now() - t0 > 1000) {
          setSyncStatus(prev => ({ ...prev, active: true, direction, current, total }));
        }
      },
      done: (warning = "") => {
        clearTimeout(timer);
        if (syncEpochRef.current !== myEpoch) return;
        setSyncStatus({ active: false, direction: "", current: 0, total: 0, warning });
      },
    };
  }, []);

  const reverseSync = useCallback(async () => enqueueSync(async () => {
    const handle = dirHandleRef.current;
    const mySession = sid();
    if (!handle || !mySession) return;
    const myEpoch = syncEpochRef.current;
    const cancelled = () => syncEpochRef.current !== myEpoch || dirHandleRef.current !== handle;
    if (cancelled()) return;
    try {
      const m = await buildManifest(handle, mySession);
      if (cancelled()) return;
      const budget = checkBudget(m);
      if (!budget.ok) {
        setSyncStatus(prev => ({ ...prev, warning: budget.warning }));
        return;
      }

      const clientManifest = [...m.files.values()].map(f => f.entry);
      const diff = stripAttachments(await syncDiff(mySession, clientManifest, m.dirs));
      if (cancelled()) return;

      const uploadPaths = [
        ...diff.onlyLocal.map(e => e.path),
        ...diff.different.map(e => e.path),
      ];
      const tracker = uploadPaths.length > 3 ? startSyncStatus("upload", uploadPaths.length) : null;
      for (let i = 0; i < uploadPaths.length; i += 20) {
        if (cancelled()) { tracker?.done(); return; }
        const batchPaths = uploadPaths.slice(i, i + 20);
        const batch: UploadEntry[] = [];
        for (const p of batchPaths) {
          const f = m.files.get(p);
          if (f) batch.push({ path: p, data: await readForUpload(f) });
        }
        if (batch.length > 0) await uploadFiles(mySession, batch, newOpId());
        tracker?.update(Math.min(i + 20, uploadPaths.length));
      }

      if (cancelled()) { tracker?.done(); return; }
      if (diff.onlyServer.length > 0) {
        const paths = diff.onlyServer.map(e => e.path);
        await deleteFilesApi(mySession, paths, newOpId());
      }
      for (const d of diff.dirsOnlyLocal) {
        if (cancelled()) break;
        try { await mkdirRemote(mySession, d, newOpId()); } catch {}
      }
      for (const d of diff.dirsOnlyServer) {
        if (cancelled()) break;
        try { await rmdirRemote(mySession, d, newOpId()); } catch {}
      }

      tracker?.done(budget.warning);
      if (!tracker && budget.warning) setSyncStatus(prev => ({ ...prev, warning: budget.warning }));
      if (cancelled()) return;

      const snap: Record<string, string> = {};
      for (const f of m.files.values()) snap[f.entry.path] = f.entry.hash;
      snapshotRef.current = snap;
      setUploadedPaths(new Set(m.files.keys()));
      await refreshFileList();
    } catch (e) {
      console.error("Reverse sync error:", e);
    }
  }), [refreshFileList, startSyncStatus]);

  const forwardSync = useCallback(async () => enqueueSync(async () => {
    const handle = dirHandleRef.current;
    const mySession = sid();
    if (!handle || !mySession) return;
    const myEpoch = syncEpochRef.current;
    const cancelled = () => syncEpochRef.current !== myEpoch || dirHandleRef.current !== handle;
    if (cancelled()) return;
    try {
      const m = await buildManifest(handle, mySession);
      if (cancelled()) return;
      const clientManifest = [...m.files.values()].map(f => f.entry);
      const diff = stripAttachments(await syncDiff(mySession, clientManifest, m.dirs));
      if (cancelled()) return;

      // Guard against a wiped/dead server workspace deleting the user's
      // originals. If the server has nothing of ours (every local file is
      // onlyLocal, nothing onlyServer/different) yet we hold a non-empty
      // snapshot of previously-synced files, the workspace was wiped —
      // session deleted, container restarted, fd exhaustion, 503 — not a
      // user intent to clear the folder. Bail without touching local files.
      const serverEmpty =
        diff.onlyServer.length === 0 &&
        diff.different.length === 0 &&
        diff.dirsOnlyServer.length === 0 &&
        diff.onlyLocal.length === clientManifest.length;
      if (serverEmpty && clientManifest.length > 0 && Object.keys(snapshotRef.current).length > 0) {
        console.warn("Forward sync: server workspace empty while local has files — treating as server-side wipe, skipping local deletes");
        return;
      }

      const toDownload = [
        ...diff.onlyServer.map(e => e.path),
        ...diff.different.map(e => e.path),
      ].filter(p => !pendingLocalRef.current.has(p));

      const tracker = toDownload.length > 3 ? startSyncStatus("download", toDownload.length) : null;
      const newSnapshot: Record<string, string> = {};
      const changedSet = new Set(diff.different.map(e => e.path));
      const onlyLocalSet = new Set(diff.onlyLocal.map(e => e.path));
      for (const f of m.files.values()) {
        if (!changedSet.has(f.entry.path) && !onlyLocalSet.has(f.entry.path)) {
          newSnapshot[f.entry.path] = f.entry.hash;
        }
      }

      let done = 0;
      for (const path of toDownload) {
        if (cancelled()) { tracker?.done(); return; }
        try {
          const res = await getFileContent(mySession, path);
          suppressLocal(path);
          await writeFileToLocal(handle, path, res.content, res.encoding);
          const hash = await hashContent(res.content, res.encoding);
          newSnapshot[path] = hash;
        } catch {}
        done++;
        tracker?.update(done);
      }
      if (cancelled()) { tracker?.done(); return; }

      for (const e of diff.onlyLocal) {
        // Re-check on every iteration: a disconnect (e.g. session delete)
        // mid-loop must stop further local deletions immediately.
        if (cancelled()) return;
        if (snapshotRef.current[e.path]) {
          suppressLocal(e.path);
          await deleteLocalFile(handle, e.path);
        } else {
          newSnapshot[e.path] = e.hash;
        }
      }

      for (const d of diff.dirsOnlyServer) {
        try {
          const parts = d.split("/");
          let dir = handle;
          let acc = "";
          for (const part of parts) {
            acc = acc ? `${acc}/${part}` : part;
            suppressLocal(acc);
            dir = await dir.getDirectoryHandle(part, { create: true });
          }
        } catch {}
      }

      snapshotRef.current = newSnapshot;
      setUploadedPaths(new Set(Object.keys(newSnapshot)));
      await refreshFileList();
      tracker?.done();
    } catch (e) {
      console.error("Forward sync error:", e);
    }
  }), [refreshFileList, startSyncStatus]);

  const processLocalChanges = useCallback(async () => enqueueSync(async () => {
    const handle = dirHandleRef.current;
    const mySession = sid();
    if (!handle || !mySession) return;
    const myEpoch = syncEpochRef.current;
    const cancelled = () => syncEpochRef.current !== myEpoch || dirHandleRef.current !== handle;
    const changes = new Map(pendingLocalRef.current);
    pendingLocalRef.current.clear();

    const toUpload: UploadEntry[] = [];
    const toDelete: string[] = [];

    for (const [path, { event: eventType, isDir }] of changes) {
      if (cancelled()) return;
      if (consumeLocalSuppress(path)) continue;

      if (isDir) {
        if (eventType === "appeared") {
          try { await mkdirRemote(mySession, path, newOpId()); } catch {}
        } else if (eventType === "disappeared") {
          try { await rmdirRemote(mySession, path, newOpId()); } catch {}
        }
        continue;
      }

      if (eventType === "disappeared") {
        if (snapshotRef.current[path]) {
          toDelete.push(path);
          delete snapshotRef.current[path];
        } else {
          try { await rmdirRemote(mySession, path, newOpId()); } catch {}
        }
        continue;
      }

      try {
        const file = await getFileByPath(handle, path);
        if (!file || file.size === 0) continue;
        if (file.size > MAX_FILE_SIZE) continue;

        const ext = path.split(".").pop()?.toLowerCase() || "";
        const entry: UploadEntry = BINARY_EXTENSIONS.has(ext)
          ? { path, data: new Blob([await file.arrayBuffer()]) }
          : { path, data: new Blob([await file.text()]) };

        const hash = await hashData(entry.data);
        if (snapshotRef.current[path] === hash) continue;
        snapshotRef.current[path] = hash;
        toUpload.push(entry);
      } catch {}
    }

    if (toUpload.length > 0) {
      const tracker = toUpload.length > 3 ? startSyncStatus("upload", toUpload.length) : null;
      for (let i = 0; i < toUpload.length; i += 20) {
        if (cancelled()) { tracker?.done(); return; }
        const batch = toUpload.slice(i, i + 20);
        await uploadFiles(mySession, batch, newOpId());
        tracker?.update(Math.min(i + 20, toUpload.length));
      }
      tracker?.done();
    }
    if (cancelled()) return;
    if (toDelete.length > 0) await deleteFilesApi(mySession, toDelete, newOpId());

    if (toUpload.length > 0 || toDelete.length > 0) {
      setUploadedPaths(prev => {
        const next = new Set(prev);
        for (const f of toUpload) next.add(f.path);
        for (const p of toDelete) next.delete(p);
        return next;
      });
    }
    await refreshFileList();
  }), [refreshFileList, startSyncStatus]);

  const processContainerChanges = useCallback(async () => enqueueSync(async () => {
    const handle = dirHandleRef.current;
    const mySession = sid();
    if (!handle || !mySession) return;
    const myEpoch = syncEpochRef.current;
    const cancelled = () => syncEpochRef.current !== myEpoch || dirHandleRef.current !== handle;

    const changes = new Map(pendingContainerRef.current);
    pendingContainerRef.current.clear();

    try {
      const added: string[] = [];
      const removed: string[] = [];
      const fileChanges = [...changes].filter(([, { isDir }]) => !isDir);
      const tracker = fileChanges.length > 3 ? startSyncStatus("download", fileChanges.length) : null;
      let done = 0;

      for (const [path, { event: eventType, isDir }] of changes) {
        if (cancelled()) { tracker?.done(); return; }
        const isDelete = eventType.includes("delete") || eventType.includes("moved_from");
        const isCreate = eventType.includes("create") || eventType.includes("moved_to");

        if (isDir) {
          if (isCreate) {
            try {
              const parts = path.split("/");
              let dir = handle;
              let acc = "";
              for (const part of parts) {
                acc = acc ? `${acc}/${part}` : part;
                suppressLocal(acc);
                dir = await dir.getDirectoryHandle(part, { create: true });
              }
            } catch {}
          } else if (isDelete) {
            try {
              const parts = path.split("/");
              let dir = handle;
              for (let i = 0; i < parts.length - 1; i++) dir = await dir.getDirectoryHandle(parts[i]!);
              suppressLocal(path);
              await dir.removeEntry(parts[parts.length - 1]!, { recursive: true });
            } catch {}
          }
          continue;
        }

        if (isDelete) {
          if (snapshotRef.current[path]) {
            suppressLocal(path);
            await deleteLocalFile(handle, path);
            delete snapshotRef.current[path];
            removed.push(path);
          }
          done++;
          tracker?.update(done);
          continue;
        }

        try {
          const res = await getFileContent(mySession, path);
          const hash = await hashContent(res.content, res.encoding);
          if (snapshotRef.current[path] === hash) { done++; continue; }
          suppressLocal(path);
          await writeFileToLocal(handle, path, res.content, res.encoding);
          snapshotRef.current[path] = hash;
          added.push(path);
        } catch {
          // Fetch failure (transient 404, container restarting, session torn
          // down) is NOT a delete signal — only an explicit delete event is.
          // Treating it as one would wipe the user's original off disk.
        }
        done++;
        tracker?.update(done);
      }

      tracker?.done();

      if (added.length > 0 || removed.length > 0) {
        setUploadedPaths(prev => {
          const next = new Set(prev);
          for (const p of added) next.add(p);
          for (const p of removed) next.delete(p);
          return next;
        });
      }
      await refreshFileList();
    } catch {}
  }), [refreshFileList, startSyncStatus]);

  const startLocalWatch = useCallback((handle: FileSystemDirectoryHandle) => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if ("FileSystemObserver" in window) {
      const observer = new FileSystemObserver((records) => {
        for (const record of records) {
          if (record.type === "unknown" || record.type === "errored") {
            reverseSync();
            return;
          }
          const path = nfc(record.relativePathComponents.join("/"));
          if (!path) continue;
          const firstDir = path.split("/")[0] ?? "";
          if (SKIP_DIRS.has(firstDir)) continue;
          if (shouldSkipFile(path.split("/").pop() || "")) continue;
          if (consumeLocalSuppress(path)) continue;
          const isDir = record.changedHandle?.kind === "directory";
          pendingLocalRef.current.set(path, { event: record.type, isDir });
        }
        if (localDebounceRef.current) clearTimeout(localDebounceRef.current);
        localDebounceRef.current = window.setTimeout(processLocalChanges, DEBOUNCE_MS);
      });
      observer.observe(handle, { recursive: true });
      observerRef.current = observer;
    } else {
      pollTimerRef.current = window.setInterval(reverseSync, POLL_INTERVAL_MS);
    }
  }, [reverseSync, processLocalChanges]);

  const stopLocalWatch = useCallback(() => {
    if (observerRef.current) { observerRef.current.disconnect(); observerRef.current = null; }
    if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
    if (localDebounceRef.current) { clearTimeout(localDebounceRef.current); localDebounceRef.current = null; }
  }, []);

  const startContainerWatch = useCallback(() => {
    const mySession = sid();
    if (!mySession) return;
    if (fileWatchRef.current) { fileWatchRef.current.close(); fileWatchRef.current = null; }
    if (containerDebounceRef.current) { clearTimeout(containerDebounceRef.current); containerDebounceRef.current = null; }
    pendingContainerRef.current.clear();
    fileWatchRef.current = subscribeFileWatch(
      mySession,
      (data) => {
        if (!dirHandleRef.current) return;
        if (isOwnOp(data.opId)) return;
        const path = nfc(data.path);
        // Attachments live outside the folder mirror — never pull them into
        // the user's local folder when the "+" upload echoes back over SSE.
        if (isAttachmentPath(path)) return;
        if (data.hash && snapshotRef.current[path] === data.hash) return;
        pendingContainerRef.current.set(path, { event: data.event, isDir: !!data.isDir });
        if (containerDebounceRef.current) clearTimeout(containerDebounceRef.current);
        containerDebounceRef.current = window.setTimeout(processContainerChanges, DEBOUNCE_MS);
      },
      () => { forwardSync(); },
      -1,
    );
  }, [processContainerChanges, forwardSync]);

  const stopContainerWatch = useCallback(() => {
    if (fileWatchRef.current) { fileWatchRef.current.close(); fileWatchRef.current = null; }
    if (containerDebounceRef.current) { clearTimeout(containerDebounceRef.current); containerDebounceRef.current = null; }
  }, []);

  const initialUpload = reverseSync;

  const finishConnect = useCallback(async (handle: FileSystemDirectoryHandle, mode: "local" | "server" | "merge") => {
    setConflict(null);

    syncEpochRef.current++;
    stopLocalWatch();
    stopContainerWatch();
    pendingLocalRef.current.clear();
    pendingContainerRef.current.clear();
    pendingOpsRef.current.clear();
    suppressLocalRef.current.clear();
    snapshotRef.current = {};
    syncChainRef.current = Promise.resolve();
    setSyncStatus({ active: false, direction: "", current: 0, total: 0, warning: "" });

    setDirHandle(handle);
    dirHandleRef.current = handle;
    setDocConnected(true);
    setConnectedSessionId(sessionIdRef.current);

    const mySession = sid();
    if (mySession) {
      if (mode === "server") {
        const { listServerFiles } = await import("../api/files");
        const serverFiles = (await listServerFiles(mySession)).filter((p) => !isAttachmentPath(p));
        for (const path of serverFiles) {
          try {
            const res = await getFileContent(mySession, path);
            await writeFileToLocal(handle, path, res.content, res.encoding);
          } catch {}
        }
      } else if (mode === "local") {
        await clearWorkspace(mySession);
      }
      await initialUpload();
    }

    startLocalWatch(handle);
    if (mySession) startContainerWatch();

    await refreshFileList();
  }, [refreshFileList, startLocalWatch, startContainerWatch, stopLocalWatch, stopContainerWatch, initialUpload]);

  const pickFolder = useCallback(async () => {
    try {
      const handle = await (window as unknown as { showDirectoryPicker: (opts: { mode: string }) => Promise<FileSystemDirectoryHandle> }).showDirectoryPicker({ mode: "readwrite" });
      const mySession = sid();
      if (mySession) {
        const { listServerFiles } = await import("../api/files");
        // Attachments aren't part of the folder mirror, so they must not
        // trigger the merge/replace conflict prompt.
        const serverFiles = (await listServerFiles(mySession)).filter((p) => !isAttachmentPath(p));
        if (serverFiles.length > 0) {
          setConflict({ handle, serverCount: serverFiles.length });
          return;
        }
      }
      await finishConnect(handle, "local");
    } catch (e: unknown) {
      const name = (e as { name?: string })?.name;
      if (name !== "AbortError") console.error("Folder pick error:", e);
    }
  }, [finishConnect]);

  const disconnect = useCallback(() => {
    syncEpochRef.current++;
    stopLocalWatch();
    stopContainerWatch();
    setDirHandle(null);
    dirHandleRef.current = null;
    setConnectedSessionId(null);
    setDocFiles([]);
    setDocConnected(false);
    snapshotRef.current = {};
    pendingLocalRef.current.clear();
    pendingContainerRef.current.clear();
    pendingOpsRef.current.clear();
    suppressLocalRef.current.clear();
    syncChainRef.current = Promise.resolve();
    setUploadedPaths(new Set());
    setSyncStatus({ active: false, direction: "", current: 0, total: 0, warning: "" });
  }, [stopLocalWatch, stopContainerWatch]);

  const prevStreaming = useRef(false);
  useEffect(() => {
    if (prevStreaming.current && !streaming && docConnected) {
      forwardSync();
    }
    prevStreaming.current = streaming;
  }, [streaming, docConnected, forwardSync]);

  // Per-session independent folders: when sessionId changes, drop the current
  // connection so ChatPage can restore (or leave empty) the next session's own
  // handle. Without this we'd cross-pollinate — uploading the previous
  // session's folder into the new session's server workspace.
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (prevSessionIdRef.current === sessionId) return;
    prevSessionIdRef.current = sessionId;
    if (dirHandleRef.current) {
      syncEpochRef.current++;
      stopLocalWatch();
      stopContainerWatch();
      pendingLocalRef.current.clear();
      pendingContainerRef.current.clear();
      pendingOpsRef.current.clear();
      suppressLocalRef.current.clear();
      snapshotRef.current = {};
      syncChainRef.current = Promise.resolve();
      setDirHandle(null);
      dirHandleRef.current = null;
      setConnectedSessionId(null);
      setDocFiles([]);
      setDocConnected(false);
      setUploadedPaths(new Set());
      setSyncStatus({ active: false, direction: "", current: 0, total: 0, warning: "" });
    }
  }, [sessionId, stopLocalWatch, stopContainerWatch]);

  useEffect(() => {
    return () => { stopLocalWatch(); stopContainerWatch(); };
  }, [stopLocalWatch, stopContainerWatch]);

  return {
    dirHandle,
    connectedSessionId,
    docFiles,
    docConnected,
    uploadedPaths,
    syncStatus,
    conflict,
    pickFolder,
    disconnect,
    reverseSync,
    finishConnect,
    dismissConflict: () => setConflict(null),
    dismissWarning: () => setSyncStatus(prev => ({ ...prev, warning: "" })),
  };
}
